import React, { useState, useEffect, useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
  AreaChart,
  Area,
  Legend,
  PieChart,
  Pie
} from "recharts";
import styles from "../../../styles/case-manager-modules/overveiw-reports.module.css";
import { useQuery } from "@tanstack/react-query";
import {
  getAllCaseManagers,
  getCaseManagerSummary,
} from "../../../services/admin/case-managers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import Spinner from "../../../components/spinner";
import { 
  User, 
  Phone, 
  Clock, 
  Users, 
  Activity, 
  BarChart2
} from "lucide-react";
import { formatDate } from "../../../utils/helper";
import { getUserIdentity } from "../../../utils/authorization";
import {getUserPosition} from "../../../utils/authorization";

/* --- Colors & Constants --- */
const COLORS = {
  // Primary palette
  navy: "#1e3a8a",
  navyLight: "#60a5fa",
  slate: "#334155",
  slateLight: "#94a3b8",

  // Semantic colors - Compliance hierarchy (best to worst)
  emerald: "#10b981",      // Best compliance - fully meeting goals
  teal: "#14b8a6",         // Good compliance - above threshold
  sky: "#0ea5e9",          // Moderate - meeting minimum
  amber: "#f59e0b",        // Warning - near compliance
  stone: "#a8a29e",        // Neutral/inactive

  // Legacy (kept for backwards compatibility)
  green: "#10b981",
  greenLight: "#86efac",
  red: "#ef4444",
  orange: "#f97316",
  yellow: "#eab308",
  gray: "#bbbbbb"
};

/* --- Helper Function --- */
const parseTitle = (title) => {
  const match = title.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (match) {
    return { mainTitle: match[1].trim(), badge: match[2].trim() };
  }
  return { mainTitle: title, badge: null };
};

const getBadgeClass = (badge) => {
  if (!badge) return '';
  const badgeLower = badge.toLowerCase();
  if (badgeLower === 'current') return styles.badgeCurrent;
  if (badgeLower === 'mtd') return styles.badgeMtd;
  if (badgeLower === 'all time') return styles.badgeAllTime;
  return styles.badgeDefault;
};

/* --- Sub-Components --- */

const KPICard = ({ title, value, subtext, isTimer = false, icon: Icon }) => {
  const { mainTitle, badge } = parseTitle(title);
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiHeader}>
        <div className={styles.kpiTitleRow}>
          <h3 className={styles.kpiTitle}>{mainTitle}</h3>
          {badge && <span className={`${styles.timeBadge} ${getBadgeClass(badge)}`}>{badge}</span>}
        </div>
        {Icon && <Icon size={18} className={styles.kpiIcon} />}
      </div>
      <div>
        <div className={styles.kpiValueRow}>
          <p className={`${styles.kpiValue} ${isTimer ? styles.kpiValueTimer : ""}`}>
            {value ?? "0"}
          </p>
        </div>
        {subtext && <p className={styles.kpiSubtext}>{subtext}</p>}
      </div>
    </div>
  );
};

const SecondaryMetricCard = ({ title, value, unit, description }) => {
  const { mainTitle, badge } = parseTitle(title);
  return (
    <div className={styles.secondaryCard}>
      <div className={styles.secondaryTitleRow}>
        <p className={styles.secondaryTitle}>{mainTitle}</p>
        {badge && <span className={`${styles.timeBadgeSmall} ${getBadgeClass(badge)}`}>{badge}</span>}
      </div>
      <div className={styles.secondaryValueRow}>
        <span className={styles.secondaryValue}>{value ?? "--"}</span>
        {unit && <span className={styles.secondaryUnit}>{unit}</span>}
      </div>
      {description && <p className={styles.secondaryDesc}>{description}</p>}
    </div>
  );
};

const ProgramBannerStat = ({ value, label }) => {
  const { mainTitle, badge } = parseTitle(label);
  return (
    <div className={styles.bannerStatGroup}>
      <div className={styles.bannerStatValueRow}>
        <p className={styles.bannerStatValue}>{value ?? "0"}</p>
      </div>
      <div className={styles.bannerLabelRow}>
        <p className={styles.bannerStatLabel}>{mainTitle}</p>
        {badge && <span className={`${styles.timeBadgeSmall} ${getBadgeClass(badge)}`}>{badge}</span>}
      </div>
    </div>
  );
};

/*
   Format time bucket keys into readable labels
*/
const formatBucketLabel = (key) => {
  switch (key) {
    case "<20": return "< 20 min";
    case "20": return "20-39 min";
    case "40": return "40-59 min";
    case "60": return "60-79 min";
    case "80": return "80+ min";
    default: return `${key} min`;
  }
};

/*
   Custom Tooltip for Donut Charts
   Shows patient count and description on hover
   Shows time distribution buckets for Documented and Finalized segments
*/
const CustomDonutTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className={styles.donutTooltip}>
        <div className={styles.tooltipHeader}>
          <span
            className={styles.tooltipColorDot}
            style={{ backgroundColor: data.color }}
          />
          <span className={styles.tooltipLabel}>{data.label}</span>
        </div>
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipRow}>
            <Users size={14} className={styles.tooltipIcon} />
            <span className={styles.tooltipCount}>{data.count ?? 0}</span>
            <span className={styles.tooltipCountLabel}>patients</span>
          </div>
          {data.description && (
            <p className={styles.tooltipDescription}>{data.description}</p>
          )}
          {data.timeBuckets && (
            <div className={styles.tooltipBuckets}>
              <div className={styles.tooltipBucketsTitle}>Time Distribution</div>
              {Object.entries(data.timeBuckets).map(([key, value]) => (
                <div key={key} className={styles.tooltipBucketRow}>
                  <span className={styles.tooltipBucketLabel}>{formatBucketLabel(key)}</span>
                  <span className={styles.tooltipBucketValue}>{value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

/*
   Custom Label for Donut Segments
   Renders percentage inside each segment
*/
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }) => {
  if (percentage < 5) return null; // Don't show label for very small segments

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      style={{
        fontSize: '11px',
        fontWeight: '600',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }}
    >
      {`${percentage}%`}
    </text>
  );
};

/*
   EmptyDonutState:
   Displays a placeholder when there's no data for the donut chart.
*/
const EmptyDonutState = ({ title }) => (
  <div className={styles.chartCard}>
    <div className={styles.chartHeader}>
      <h3 className={styles.chartTitle}>{title}</h3>
    </div>
    <div className={styles.chartContainer}>
      <div className={styles.emptyDonutWrapper}>
        <div className={styles.emptyDonutRing}>
          <svg viewBox="0 0 100 100" className={styles.emptyDonutSvg}>
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="12"
              className={styles.emptyDonutCircle}
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#cbd5e1"
              strokeWidth="1"
              strokeDasharray="4 6"
              className={styles.emptyDonutDashes}
            />
          </svg>
          <div className={styles.emptyDonutCenter}>
            <div className={styles.emptyDonutIcon}>
              <Users size={20} />
            </div>
            <span className={styles.emptyDonutValue}>0</span>
            <span className={styles.emptyDonutLabel}>patients</span>
          </div>
        </div>
        <div className={styles.emptyDonutMessage}>
          <p className={styles.emptyDonutText}>No data available for this period</p>
          <p className={styles.emptyDonutSubtext}>Patient activity will appear here once recorded</p>
        </div>
      </div>
    </div>
  </div>
);

/*
   DonutChartBox:
   Displays data in a Donut (Pie) chart format with a legend.
*/
const DonutChartBox = ({ title, data }) => {
  const total = useMemo(() => {
    return data.reduce((sum, item) => sum + (item.count || 0), 0);
  }, [data]);

  const hasData = useMemo(() => {
    return data.some(item => (item.percentage || 0) > 0);
  }, [data]);

  if (!hasData || total === 0) {
    return <EmptyDonutState title={title} />;
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
      </div>
      <div className={styles.chartContainer}>
        <div className={styles.donutWrapper}>
          <div className={styles.donutCenterLabel}>
            <span className={styles.donutCenterValue}>{total}</span>
            <span className={styles.donutCenterText}>patients</span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                dataKey="percentage"
                nameKey="label"
                label={renderCustomLabel}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke={entry.color}
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomDonutTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                formatter={(value) => (
                  <span style={{ color: '#475569', fontWeight: 500 }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const MonitoringAreaChart = ({ data }) => {
  const maxY = useMemo(() => {
    if (!data || data.length === 0) return 1;
    const max = Math.max(...data.map(d => Math.max(d.rpm || 0, d.ccm || 0)));
    return Math.max(Math.ceil(max), 1);
  }, [data]);

  const yTicks = useMemo(() => {
    const step = maxY <= 5 ? 1 : maxY <= 10 ? 2 : maxY <= 20 ? 5 : 10;
    return Array.from({ length: Math.floor(maxY / step) + 1 }, (_, i) => i * step);
  }, [maxY]);

  return (
  <div className={styles.fullWidthChartCard}>
    <div className={styles.chartHeader}>
      <h3 className={styles.chartTitle}>Monitoring Time Distribution (MTD)</h3>
      <p className={styles.chartSubtitle}>Patient count by minutes captured</p>
    </div>
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
        >
          <defs>
            <linearGradient id="colorRpm" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.navy} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.navy} stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorCcm" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.green} stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={true} stroke="#e2e8f0" />
          <XAxis
            dataKey="time"
            label={{ value: 'Minutes', position: 'insideBottom', offset: -10, fill: '#64748b' }}
            tick={{ fill: '#64748b', fontSize: 12 }}
            domain={[0, 'auto']}
            type="number"
            tickCount={9}
          />
          <YAxis
            label={{ value: 'Number of Patients', angle: -90, position: 'insideLeft', offset: 10, fill: '#64748b', style: {textAnchor: 'middle'} }}
            tick={{ fill: '#64748b', fontSize: 12 }}
            domain={[0, maxY]}
            ticks={yTicks}
            allowDecimals={false}
          />
          <Tooltip 
            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            labelFormatter={(label) => `${label} Minutes`}
          />
          <Legend verticalAlign="top" height={36} iconType="circle" />
          <Area 
            type="monotone" 
            dataKey="ccm" 
            name="CCM Patients"
            stroke={COLORS.green} 
            fillOpacity={0.5} 
            fill="url(#colorCcm)" 
            strokeWidth={4}
          />
          <Area 
            type="monotone" 
            dataKey="rpm" 
            name="RPM Patients"
            stroke={COLORS.navy} 
            fillOpacity={0.2} 
            fill="url(#colorRpm)" 
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
  );
};

/* --- Main Component --- */

const OverviewReports = () => {
  // --- Identity & Role Check (must be first) ---
  const identity = getUserIdentity();
  const isNonTocaCareManager = identity.isNonToca && identity.isCareManager;

  // If NonToca CareManager, seed directly from localStorage via identity.staff_id
  // Otherwise start as null and let the useEffect pick the first CM from the list
  const [selectedCaseManager, setSelectedCaseManager] = useState(() => {
    if (isNonTocaCareManager) {
      return identity.staff_id ?? null;
    }
    return null;
  });

  // 1. Fetching List of Case Managers — skipped entirely for NonToca CMs
  const { data: caseManagers, isLoading: isCaseManagersLoading } = useQuery(
    ["all-care-managers"],
    getAllCaseManagers,
    {
      enabled: !isNonTocaCareManager, // no need to fetch the list for NonToca CMs
      select: (data) => data.filter((cm) => cm.first_name),
    }
  );

  // 2. Default Selection — only runs for non-NonToca CMs
  useEffect(() => {
    if (!isNonTocaCareManager && caseManagers?.length > 0 && !selectedCaseManager) {
      setSelectedCaseManager(caseManagers[0]?.id);
    }
  }, [caseManagers, selectedCaseManager, isNonTocaCareManager]);

  // 3. Fetching Summary Data
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery(
    ["care-manager-summary", selectedCaseManager],
    () => getCaseManagerSummary(selectedCaseManager),
    {
      enabled: !!selectedCaseManager,
      cacheTime: 0,
    }
  );

  // --- Data Processing & Memos ---

  // User Info
  const userFullName = `${summaryData?.first_name || ""} ${summaryData?.last_name || ""}`;
  const userRole = summaryData?.role_slug || summaryData?.account_position?.name || summaryData?.account_roles_detail?.[0]?.name || "Staff Member";
  const userPhone = summaryData?.phone_number || "--";
  const lastLogin = summaryData?.last_login ? formatDate(summaryData.last_login) : "--";

  // Banner Metrics
  const rpmTotal = summaryData?.patient_summary?.rpm_patient_count ?? 0;
  const rpmOnboarded = summaryData?.patient_summary?.rpm_onboarded_this_month ?? 0;
  const ccmTotal = summaryData?.patient_summary?.ccm_patient_count ?? 0;
  const carePlansPercent = summaryData?.rpm_ccm_summary?.ccm_compliance?.completed?.percentage ?? 0;

  // MTD Patient Totals (for banner display, matching donut centers)
  const rpmComplianceMtdTotal = useMemo(() => {
    const source = summaryData?.rpm_ccm_summary?.rpm_compliance;
    if (!source) return 0;
    return (source["16_plus_days"]?.count || 0) +
           (source["2_plus_days"]?.count || 0) +
           (source["near_compliant"]?.count || 0) +
           (source["non_compliant"]?.count || 0);
  }, [summaryData]);

  const ccmComplianceMtdTotal = useMemo(() => {
    const source = summaryData?.rpm_ccm_summary?.ccm_compliance;
    if (!source) return 0;
    return (source.ccm_compliant?.total || 0) +
           (source.finalized_but_not_compliant?.total || 0) +
           (source.documented?.total || 0) +
           (source.no_careplan?.count || 0);
  }, [summaryData]);
  
  const statusSummary = summaryData?.status_summary?.status_summary || [];
  const complianceObj = statusSummary.find(s => s.status === "compliant");
  const complianceRate = complianceObj ? complianceObj.percentage : 0;

  // --- Chart Data ---

  // 1. RPM Compliance Data (Donut)
  const rpmComplianceData = useMemo(() => {
      const source = summaryData?.rpm_ccm_summary?.rpm_compliance;
      if (!source) return [];

      return [
        {
          label: "16+ Days & 20m",
          percentage: source["16_plus_days"]?.percentage || 0,
          count: source["16_plus_days"]?.count || 0,
          description: source["16_plus_days"]?.description || "Full billing compliance achieved",
          color: COLORS.emerald
        },
        {
          label: "2+ Days & 20m",
          percentage: source["2_plus_days"]?.percentage || 0,
          count: source["2_plus_days"]?.count || 0,
          description: source["2_plus_days"]?.description || "Minimum billing threshold met",
          color: COLORS.teal
        },
        {
          label: "Near Compliant",
          percentage: source["near_compliant"]?.percentage || 0,
          count: source["near_compliant"]?.count || 0,
          description: source["near_compliant"]?.description || "Close to meeting requirements",
          color: COLORS.amber
        },
        {
          label: "Non-Compliant",
          percentage: source["non_compliant"]?.percentage || 0,
          count: source["non_compliant"]?.count || 0,
          description: source["non_compliant"]?.description || "Not meeting compliance criteria",
          color: COLORS.stone
        }
      ];
  }, [summaryData]);

  // 2. RPM Engagement Data (Donut)
  const rpmEngagementData = useMemo(() => {
    const source = summaryData?.rpm_ccm_summary?.rpm_engagement;
    if (!source) return [];

    return [
      {
        label: "Successful 2-Way",
        percentage: source.two_way?.percentage || 0,
        count: source.two_way?.count || 0,
        description: "Direct patient communication achieved",
        color: COLORS.emerald
      },
      {
        label: "Repeated Contact",
        percentage: source.repeated_contact?.percentage || 0,
        count: source.repeated_contact?.patients || 0,
        description: "Multiple contact attempts made",
        color: COLORS.teal
      },
      {
        label: "Voicemails",
        percentage: source.voicemail?.percentage || 0,
        count: source.voicemail?.count || 0,
        description: "Voicemail messages left",
        color: COLORS.amber
      },
      {
        label: "No Attempts",
        percentage: source.no_attempt?.percentage || 0,
        count: source.no_attempt?.count || 0,
        description: "No contact attempts recorded",
        color: COLORS.stone
      }
    ];
  }, [summaryData]);

  // 3. CCM Compliance Data (Donut)
  const ccmComplianceData = useMemo(() => {
    const source = summaryData?.rpm_ccm_summary?.ccm_compliance;
    if (!source) return [];

    return [
      {
        label: "CCM Compliant (≥40m)",
        percentage: source.ccm_compliant?.percentage || 0,
        count: source.ccm_compliant?.total || 0,
        description: source.ccm_compliant?.description || "Full CCM billing compliance",
        color: COLORS.emerald
      },
      {
        label: "Finalized (<40m)",
        percentage: source.finalized_but_not_compliant?.percentage || 0,
        count: source.finalized_but_not_compliant?.total || 0,
        description: source.finalized_but_not_compliant?.description || "Care plan finalized, time below threshold",
        color: COLORS.teal,
        timeBuckets: source.finalized_but_not_compliant?.time_buckets || null
      },
      {
        label: "Documented",
        percentage: source.documented?.percentage || 0,
        count: source.documented?.total || 0,
        description: source.documented?.description || "Documentation in progress",
        color: COLORS.amber,
        timeBuckets: source.documented?.time_buckets || null
      },
      {
        label: "No Care Plan",
        percentage: source.no_careplan?.percentage || 0,
        count: source.no_careplan?.count || 0,
        description: source.no_careplan?.description || "Care plan not yet created",
        color: COLORS.stone
      }
    ];
  }, [summaryData]);

  // 4. Monitoring Time (Area Chart) with Clipping Fix
  const monitoringTimeChartData = useMemo(() => {
    const rawData = summaryData?.rpm_ccm_summary?.rpm_ccm_time_distribution?.monitoring_time_distribution || [];
    
    if (rawData.length === 0) return [];

    const processedData = [...rawData];
    const lastPoint = processedData[processedData.length - 1];
    
    if (lastPoint && lastPoint.time === 80) {
        processedData.push({ time: 82, rpm: 0, ccm: 0 });
    }

    return processedData;
  }, [summaryData]);

  // --- KPI Calculations ---
  
  const engagementRatio = summaryData?.monitoring_summary?.engagement_ratio;
  const countTwoWay = engagementRatio?.two_way?.count || 0;
  const countVM = engagementRatio?.voicemail?.count || 0;
  const callSuccessRate = engagementRatio?.two_way?.percentage ?? 0;
  const ratioDisplay = `${countTwoWay} : ${countVM}`;

  // Show loading spinner only for non-NonToca CMs waiting on the CM list
  if (isCaseManagersLoading && !isNonTocaCareManager) {
    return (
      <div className={styles.loaderContainer}>
        <Spinner />
        <p className={styles.loaderText}>Loading staff list...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      
      {/* 1. User Banner & Selection */}
      <div className={styles.userBanner}>
        <div className={styles.userDetailsContainer}>
            <div className={styles.avatarCircle}>
                <User size={28} />
            </div>
            <div className={styles.userInfo}>
                <div className={styles.nameRow}>
                    <h2 className={styles.userName}>{userFullName}</h2>
                    <span className={styles.userRole}> - {userRole}</span>
                </div>
                <div className={styles.metaRow}>
                    <div className={styles.metaItem}>
                        <Phone size={14} />
                        <span>{userPhone}</span>
                    </div>
                    <div className={styles.metaItem}>
                        <Clock size={14} />
                        <span>Last Login: {lastLogin}</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Dropdown hidden for NonToca CareManagers — they only see their own data */}
        {!isNonTocaCareManager && (
          <div className={styles.userSelectContainer}>
            <Select
              value={selectedCaseManager?.toString()} 
              onChange={(val) => setSelectedCaseManager(val)}
            >
              <SelectTrigger className="w-[250px] bg-white">
                <SelectValue placeholder="Choose User">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} />
                    <span>
                      {caseManagers?.find(cm => cm.id.toString() === selectedCaseManager?.toString()) 
                        ? `${caseManagers.find(cm => cm.id.toString() === selectedCaseManager?.toString()).name}`
                        : "Choose User"}
                    </span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent searchField={true} className="max-h-[300px]">
                {caseManagers?.map((cm) => (
                  <SelectItem key={cm.id} value={cm.id.toString()}>
                    {cm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isSummaryLoading ? (
        <div className={styles.loaderContainer}>
          <Spinner />
          <p className={styles.loaderText}>Loading report data...</p>
        </div>
      ) : (
        <>
          {/* 2. Primary KPI Grid */}
          <div className={styles.kpiGrid}>
            <KPICard 
              title="Total Patients (Current)" 
              value={summaryData?.patient_summary?.patients_count} 
              icon={Users}
            />
            <KPICard
              title="RPM Patients (Current)"
              value={summaryData?.patient_summary?.rpm_patient_count}
              subtext="Total active RPM patients"
              icon={Activity}
            />
            <KPICard
              title="CCM Patients (Current)"
              value={summaryData?.patient_summary?.ccm_patient_count}
              subtext="Total active CCM patients"
              icon={Activity}
            />
            <KPICard 
              title="RPM & CCM Patients (Current)" 
              value={summaryData?.patient_summary?.rpm_ccm_intersect_count} 
              subtext="Enrolled in both programs"
              icon={Activity}
              trendColor="green"
            />
            <KPICard
              title="Out of Range (All Time)"
              value={summaryData?.monitoring_summary?.total_out_of_range_time}
              isTimer={true}
              subtext="Avg time on out of range"
              icon={Clock}
            />
          </div>

          {/* 3. Secondary Metrics */}
          <div className={styles.secondaryGrid}>
            <SecondaryMetricCard 
                title="Dailies (All Time)" 
                value={summaryData?.monitoring_summary?.daily_monitoring_time}
                description="Avg daily monitoring"
            />
            <SecondaryMetricCard 
                title="Expected Performance (MTD)" 
                value={`${summaryData?.monitoring_summary?.expected_performance || 0}%`}
                description="Completed by EOD"
            />
            <SecondaryMetricCard 
                title="Avg Time 2-Way (All Time)" 
                value={summaryData?.monitoring_summary?.avg_time_successful_TWC || "0:00"}
                description="Time in chart during call"
            />
            <SecondaryMetricCard 
                title="Avg Days Between 2-Way (All Time)" 
                value={summaryData?.monitoring_summary?.average_days_TWC || "0"}
                unit="Days"
                description="Frequency of contact"
            />
             <SecondaryMetricCard 
                title="Successful 2-Way/Day (All Time)" 
                value={summaryData?.monitoring_summary?.successful_TWC_per_day || "0"}
                description="Avg interactions per day"
            />
            <SecondaryMetricCard 
                title="Voicemails/Day (All Time)" 
                value={summaryData?.monitoring_summary?.left_voicemail_per_day || "0"}
                description="Avg voicemails left"
            />
            <SecondaryMetricCard 
                title="Call Success Rate (MTD)" 
                value={`${callSuccessRate}%`}
                description="Successful / Total Attempts"
            />
            <SecondaryMetricCard 
                title="2-Way : Voicemail (MTD)" 
                value={ratioDisplay}
                description="Engagement Ratio"
            />
          </div>

          {/* 4. RPM Section */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionTitleRow}>
                <h3 className={styles.sectionLabel}>RPM Overview</h3>
            </div>
            
            <div className={styles.programBanner}>
              <ProgramBannerStat
                value={rpmTotal}
                label="Total RPM Patients (Current)"
              />
              <ProgramBannerStat
                value={summaryData?.patient_summary?.rpm_ccm_intersect_count ?? 0}
                label="RPM & CCM Patients (Current)"
              />
              <ProgramBannerStat
                value={rpmComplianceMtdTotal}
                label="Compliance Tracked (MTD)"
              />
              <ProgramBannerStat
                value={rpmOnboarded}
                label="Onboarded This Month (MTD)"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '16px' }}>
                <DonutChartBox
                    title="RPM Compliance (MTD)"
                    data={rpmComplianceData}
                />
                <DonutChartBox
                    title="RPM Engagement (MTD)"
                    data={rpmEngagementData}
                />
            </div>
          </div>

          {/* 5. CCM Section */}
          <div className={styles.sectionContainer}>
             <div className={styles.sectionTitleRow}>
                <h3 className={styles.sectionLabel}>CCM Overview</h3>
            </div>

             <div className={styles.programBanner}>
              <ProgramBannerStat
                value={ccmTotal}
                label="Total CCM Patients (Current)"
              />
              <ProgramBannerStat
                value={summaryData?.patient_summary?.rpm_ccm_intersect_count ?? 0}
                label="RPM & CCM Patients (Current)"
              />
              <ProgramBannerStat
                value={ccmComplianceMtdTotal}
                label="Compliance Tracked (MTD)"
              />
               <ProgramBannerStat
                value={summaryData?.rpm_ccm_summary?.ccm_compliance?.completed?.total ?? 0}
                label="Care Plans Completed (MTD)"
              />
               <ProgramBannerStat
                value={`${carePlansPercent}%`}
                label="% Care Plans Completed (MTD)"
                trendColor="green"
              />
            </div>

            <div style={{ marginTop: '16px' }}>
                <DonutChartBox
                    title="CCM Compliance (MTD)"
                    data={ccmComplianceData}
                />
            </div>
          </div>

          {/* 6. Monitoring Time Distribution (Area Chart) */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionTitleRow}>
               <h3 className={styles.sectionLabel}>Monitoring Time (MTD)</h3>
            </div>
            <MonitoringAreaChart data={monitoringTimeChartData} />
          </div>

        </>
      )}
    </div>
  );
};

export default OverviewReports;